/**
 * The shell: the left rail, the top bar, and the display-mode control.
 *
 * These are the words an operator reads before they read anything else, and the ones #505's
 * evaluator read first when he could not work out how to fly. Three failures here, all of the
 * same kind - the product naming itself after its own internals:
 *
 *   - the first nav entry was `COP`, an acronym expanded nowhere in the product, sitting above
 *     twelve entries named after the tables behind them rather than the work in front of them;
 *   - the health word for a system with nothing wrong was `OPERATIONAL`, which is the word a
 *     status page uses, not the answer to "is it working";
 *   - the display modes were renamed DAY and NIGHT by T1 (#520, #530) and this control was
 *     never told, so the one place an operator switches them still offered "bright mode".
 *
 * `terms.ts` holds the vocabulary; this file holds the closed sets that spend it.
 */

import { plural } from './terms';

/**
 * The rail's four groups.
 *
 * `Assets` retires with `asset` (`terms.ts`): the group holds drones, docks, pilots and sites,
 * and the only one of those an operator would call an asset is the drone, which is a drone.
 * `Review` named the act rather than the contents, and what is in it is the record of what
 * happened, so it is `Records`.
 */
export const NAV_GROUP_LABEL = {
  operations: 'Operations',
  fleet: 'Fleet',
  records: 'Records',
  system: 'System',
} as const;

export type NavGroupKey = keyof typeof NAV_GROUP_LABEL;

/**
 * Every destination in the rail, keyed by its route.
 *
 * Keyed by href rather than by a name, because the href is the thing that cannot drift: the
 * active-row resolver, the tests and this table all read from the same key, so a renamed label
 * can never quietly point at a different page.
 *
 *   COP             -> Air picture      the acronym appears nowhere else, expanded nowhere
 *   Mission Library -> Saved missions   a library is a building; these are the saved plans
 *   Flights         -> Flight log       the record, not a live thing, and it reads as one
 *
 * Kept as they are: Alerts, Drones, Docks, Pilots, Sites, Settings, Admin. Each is already the
 * canonical noun in `terms.ts` or a plain word for the page behind it.
 *
 * NOT here: `/missions/history`. It was the thirteenth rail row and is now reached from the
 * missions board, which is the page it belongs under. Its name lives with the surfaces that
 * spend it, as `PAST_MISSIONS_TITLE` in `mission.ts` - keeping it in this map would break the
 * one guarantee the href key buys, that every entry in it is a row in the rail.
 *
 * NOT here either: `/onboarding`, for the same reason. It left the rail because it is a
 * one-time checklist, not a destination an operator returns to, and it is reached from
 * Settings -> Organization. Its name is `SETUP_TITLE` below.
 *
 * NOT here either: `/compliance`. Compliance & Reports is a tab of Settings now rather than a
 * rail row (#505 note 8), so its name is the tab label in `settings/page.tsx`. Unlike the two
 * above, its route still exists as a redirect to `/settings?tab=compliance`, because unlike a
 * checklist or a sub-page it was a destination operators had bookmarked
 * (client/next.config.ts).
 */
export const NAV_ITEM_LABEL: Readonly<Record<string, string>> = {
  '/': 'Air picture',
  '/missions': 'Saved missions',
  '/alerts': 'Alerts',
  '/fleet/drones': 'Drones',
  '/fleet/docks': 'Docks',
  '/fleet/pilots': 'Pilots',
  '/sites': 'Sites',
  '/flights': 'Flight log',
  '/settings': 'Settings',
  '/admin': 'Admin',
};

/** The page title over the live picture. Retires "Common Operating Picture" with `COP`. */
export const AIR_PICTURE_TITLE = 'Air picture';

/**
 * The setup checklist's name, wherever it is named.
 *
 * `Onboarding` is the word for the product's own funnel, not for the operator's work, so the
 * page is called Setup - and now that it has no rail row, this is the only place that word is
 * decided. Two surfaces spend it: the link in Settings -> Organization that is the way in, and
 * the page's own title. The shape mirrors `AIR_PICTURE_TITLE`: a page's name is one string.
 *
 * It stays in `nav.ts` rather than moving out the way `PAST_MISSIONS_TITLE` did, because there
 * is no setup module to move it to - one constant does not earn a copy file - and because one
 * of the two surfaces is a top-bar title, which is this file's scope. If setup ever grows copy
 * of its own, this is the string that leaves with it.
 */
export const SETUP_TITLE = 'Setup';

/**
 * The one-word answer, top right, to "is this system working".
 *
 * `OPERATIONAL` is what the system says about itself; "All systems up" is what an operator
 * wants to know. `CRITICAL` keeps its capitals because it is a critical condition and
 * `docs/operational-standards.md` reserves them for exactly that. The render site applies a CSS
 * `uppercase` class to all three, so nothing changes shape here - the string is sentence case
 * so that #505's T9 has a locale that does not shout.
 */
export const SYSTEM_HEALTH_LABEL = {
  nominal: 'All systems up',
  degraded: 'Degraded',
  critical: 'CRITICAL',
  /**
   * Before the first health response lands, and after one that never arrives.
   *
   * A fourth word rather than a fourth colour. The subsystem dot is amber for
   * `unknown`, and "All systems up" beside an amber dot is a contradiction the
   * operator has to resolve by hunting for a fault that is not there. This says
   * the true thing: nothing has answered yet.
   */
  unknown: 'Checking',
} as const;

export type SystemHealthKey = keyof typeof SYSTEM_HEALTH_LABEL;

/** The health words allowed to shout, for the guard test's case rule. */
export const CRITICAL_SYSTEM_HEALTH: ReadonlySet<string> = new Set([SYSTEM_HEALTH_LABEL.critical]);

/**
 * The four dependency dots beside the clock.
 *
 * Deliberately still named after the services they watch. An operator does not act on these -
 * they are what the team reads when the operator says "it has stopped updating" - and a dot
 * renamed "drone link" would collide with the aircraft's own link, which the map chip, the dock
 * row and the drone panel all already call exactly that. What was actually wrong here was the
 * tooltip: `DB: connected · Redis: connected · MQTT: connected · WS: connected` abbreviated the
 * four names the dots had already spelled out, and separated them with a middle dot.
 */
export const DEPENDENCY_LABEL = {
  db: 'Database',
  cache: 'Cache',
  mqtt: 'MQTT',
  updates: 'Live updates',
} as const;

export type DependencyKey = keyof typeof DEPENDENCY_LABEL;

/** What a dependency's wire state is called in front of a person. Never `connected`. */
export const DEPENDENCY_STATE_LABEL = {
  connected: 'up',
  disconnected: 'down',
  unknown: 'not known',
} as const;

export type DependencyStateKey = keyof typeof DEPENDENCY_STATE_LABEL;

/** The dependency row's tooltip: four names and four plain states, in one sentence. */
export function dependencyTooltip(states: Record<DependencyKey, DependencyStateKey>): string {
  return (Object.keys(DEPENDENCY_LABEL) as DependencyKey[])
    .map((key) => `${DEPENDENCY_LABEL[key]} ${DEPENDENCY_STATE_LABEL[states[key]]}`)
    .join(', ');
}

/** The site count beside the site switcher. Through `plural`, so one site is not "1 sites". */
export function siteCount(count: number): string {
  return plural(count, 'site', 'sites');
}

/**
 * The display-mode control at the bottom of the rail.
 *
 * The modes are DAY and NIGHT (T1's D19, and the control's own return to the sidebar in D22).
 * This control was the last surface still calling them bright and dark, which is two names for
 * one thing on the one screen where the operator picks between them.
 */
export const DISPLAY_MODE = {
  /** The action, for the collapsed icon button: the mode a press takes you to. */
  switchToDay: 'Switch to day mode',
  switchToNight: 'Switch to night mode',
  /** The expanded row, where the Switch's checked state is the current mode. */
  dayRow: 'Day mode',
  dayToggle: 'Toggle day mode',
} as const;
